import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { toast } from "sonner"
import { Target, Trophy, MapPin, CalendarDays, Shield, RefreshCw, Dumbbell, Check } from "lucide-react"
import { useProfile } from "@/hooks/useProfile"
import { useCurrentBodyweight } from "@/hooks/useCurrentBodyweight"
import { useProgramBlock } from "@/hooks/useProgramBlock"
import {
  getAthleteProfile, upsertAthleteProfile, getAthleteInjuries, recordInjuryConsent, revokeInjuryConsent,
  type AthleteProfileRow, type AthleteInjuryRow,
} from "@/lib/supabase"
import {
  getGoalLabel, getLevelLabel, getPlaceLabel, getGoalsForLevel,
  TRAINING_DAYS, LEVEL_LABELS, PLACE_LABELS, PROGRAM_TRAINING_DAYS_LABEL,
  getInjuryZoneLabel, INJURY_STATUS_LABELS, formatDateFR,
} from "@/data/labels"
import { INJURY_CONSENT_VERSION, INJURY_CONSENT_LABEL, INJURY_CONSENT_DETAILS } from "@/data/injuryConsent"
import { mailtoHref } from "@/data/contact"
import { useAuth } from "@/contexts/AuthContext"
import { formatFr } from "@/lib/utils"
import { BottomNav } from "@/components/BottomNav"
import { ScreenHeader, SectionTitle, Card, Row, RowList, Note, TONE } from "@/components/profile/ui"
import { EditSheet, SheetChoices } from "@/components/profile/EditSheet"
import { NumberWheel } from "@/components/shared/NumberWheel"

// Bornes inchangées (plus de 25 kg, moins de 400 kg), pas de 0,5 kg comme la pesée.
const TARGET_WEIGHT = { min: 25.5, max: 399.5, step: 0.5, fallback: 70 }

type FieldId = "goal" | "level" | "place" | "days" | "days_info" | "target_weight" | "injuries" | "restart"

/**
 * Écran 2 — Mon programme.
 *
 * Ces six paramètres vivaient dans les Réglages, à la fois en doublon avec la carte du
 * bloc et dangereusement : ce sont les SEULS champs dont la modification régénère le
 * programme. Les regrouper ici, sous le rappel du bloc en cours, donne au client le
 * contexte qui manquait pour comprendre ce qu'il déclenche.
 */
export function ProgramSettingsPage() {
  const navigate = useNavigate()
  const { profile, loading, updateProfile } = useProfile()
  const { bodyweightKg } = useCurrentBodyweight(profile?.raw.user_id)
  const { refreshWizardData } = useAuth()
  const block = useProgramBlock()

  const [athlete, setAthlete] = useState<AthleteProfileRow | null>(null)
  /** Blessures déclarées au diagnostic (lot 3) — lecture seule ici. */
  const [injuries, setInjuries] = useState<AthleteInjuryRow[]>([])
  const [consentChecked, setConsentChecked] = useState(false)
  const [consentDetails, setConsentDetails] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [editing, setEditing] = useState<FieldId | null>(null)
  const [draft, setDraft] = useState<string>("")
  const [draftDays, setDraftDays] = useState<string[]>([])
  const [draftTarget, setDraftTarget] = useState<number>(TARGET_WEIGHT.fallback)
  const [noTarget, setNoTarget] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    getAthleteProfile().then(setAthlete).catch(() => null)
    getAthleteInjuries().then(setInjuries).catch(() => null)
  }, [])

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-[#060a13] flex items-center justify-center">
        <div className="w-9 h-9 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
      </div>
    )
  }

  const raw = profile.raw
  const hasConsent = raw.injury_consent_at != null
  const injuriesValue = injuries.length > 0
    ? truncate(injuries.map((i) => getInjuryZoneLabel(i.zone, i.zone_other)).join(", "), 22)
    : athlete?.injuries ? truncate(athlete.injuries, 22) : "Aucune"

  const open = (field: FieldId) => {
    setEditing(field)
    switch (field) {
      case "goal": setDraft(raw.goal_id); break
      case "level": setDraft(raw.level_id); break
      case "place": setDraft(raw.place); break
      case "days": setDraftDays(raw.training_days ?? []); break
      case "target_weight": {
        // Valeur existante gardée telle quelle (la molette la propose même hors grille) ;
        // sans poids cible, la molette part du poids actuel (dernière pesée, repli sur
        // le poids d'inscription).
        const current = raw.target_weight != null ? Number(raw.target_weight) : NaN
        const hasTarget = Number.isFinite(current) && current > 0
        const weight = Number(bodyweightKg ?? raw.weight)
        setNoTarget(!hasTarget)
        setDraftTarget(hasTarget ? current : Number.isFinite(weight) && weight > 0 ? weight : TARGET_WEIGHT.fallback)
        break
      }
      case "injuries":
        setDraft(athlete?.injuries ?? "")
        setConsentChecked(false)
        setConsentDetails(false)
        setConfirmRevoke(false)
        break
      default: setDraft("")
    }
  }

  const close = () => { if (!saving) setEditing(null) }

  const save = async () => {
    if (!editing || saving) return
    setSaving(true)
    try {
      if (editing === "injuries") {
        // Les blessures vivent dans athlete_profiles, à côté des 1RM et des notes coach :
        // c'est la table de données d'entraînement, pas celle du questionnaire d'inscription.
        const text = draft.trim()
        if (text === (athlete?.injuries ?? "").trim()) { setEditing(null); return }
        // Données de santé : un nouveau texte demande un accord enregistré (lot 3). La base
        // refuse d'ailleurs l'écriture sans lui (trigger). Effacer le texte reste libre.
        if (text && !hasConsent) {
          if (!consentChecked) {
            toast.error("Coche la case d'accord pour enregistrer tes blessures")
            return
          }
          await recordInjuryConsent(INJURY_CONSENT_VERSION)
          await refreshWizardData()
        }
        const updated = await upsertAthleteProfile({ injuries: text || null })
        setAthlete(updated)
        toast.success("Limitations enregistrées")
        setEditing(null)
        return
      }

      let update: Parameters<typeof updateProfile>[0] = {}
      if (editing === "goal") update = { goal_id: draft }
      else if (editing === "level") {
        // Changer de niveau peut rendre l'objectif courant impossible (les trois listes
        // sont disjointes) : on rabat sur le premier objectif du nouveau niveau plutôt
        // que de laisser un couple (niveau, objectif) sans programme au catalogue.
        const goals = getGoalsForLevel(draft)
        update = { level_id: draft, ...(goals.includes(raw.goal_id) ? {} : { goal_id: goals[0] }) }
      }
      else if (editing === "place") update = { place: draft as "gym" | "home" }
      else if (editing === "days") update = { training_days: draftDays }
      else if (editing === "target_weight") update = { target_weight: noTarget ? null : draftTarget }

      const { error } = await updateProfile(update)
      if (error) toast.error("Erreur", { description: error })
      else {
        toast.success("Paramètre mis à jour")
        setEditing(null)
        void block.refresh()
      }
    } catch (err) {
      toast.error("Erreur", { description: err instanceof Error ? err.message : undefined })
    } finally {
      setSaving(false)
    }
  }

  /**
   * Retrait de l'accord : blessures déclarées, précision et preuve d'accord effacées en
   * UNE transaction côté base (`revoke_injury_consent`) — jamais à moitié.
   */
  const revoke = async () => {
    if (saving) return
    setSaving(true)
    try {
      await revokeInjuryConsent()
      setInjuries([])
      setAthlete((a) => (a ? { ...a, injuries: null } : a))
      await refreshWizardData()
      toast.success("Accord retiré", { description: "Tes informations de blessures sont effacées." })
      setEditing(null)
    } catch (err) {
      toast.error("Erreur", { description: err instanceof Error ? err.message : undefined })
    } finally {
      setSaving(false)
    }
  }

  const toggleDay = (id: string) => {
    setDraftDays((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]))
  }

  const blockStart = raw.program_week_start
    ? new Date(raw.program_week_start).toLocaleDateString("fr-FR", { day: "numeric", month: "long" })
    : "—"
  const blockEnd = raw.program_week_start
    ? new Date(new Date(raw.program_week_start).getTime() + block.weeksTotal * 7 * 86400000)
        .toLocaleDateString("fr-FR", { day: "numeric", month: "long" })
    : "—"

  return (
    <div className="min-h-screen bg-[#060a13] pb-28 cursor-default">
      <ScreenHeader title="Mon programme" onBack={() => navigate("/dashboard/profile")} />

      {/* Rappel du bloc en cours — informatif, non cliquable : on y est déjà. */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="px-4 pt-2">
        <Card>
          <div className="p-4">
            <div className="flex items-start gap-3">
              <span className="w-[42px] h-[42px] rounded-[13px] grid place-items-center shrink-0"
                style={{ background: TONE.cyan.bg, border: `1px solid ${TONE.cyan.border}`, color: TONE.cyan.fg }}>
                <Dumbbell className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-white truncate">{block.programName}</p>
                <p className="text-[12px] text-gray-500 mt-0.5 tnum">
                  Semaine {block.weekCurrent} / {block.weeksTotal} · {block.sessionsCompleted}/{block.sessionsTotal || "—"} séances
                </p>
              </div>
            </div>

            <div className="flex gap-1 mt-4">
              {Array.from({ length: block.weeksTotal }, (_, i) => (
                <span
                  key={i}
                  className="flex-1 h-[5px] rounded-full"
                  style={{
                    background: i + 1 < block.weekCurrent ? "#06b6d4"
                      : i + 1 === block.weekCurrent ? "linear-gradient(90deg,#06b6d4 50%,rgba(255,255,255,0.08) 50%)"
                      : "rgba(255,255,255,0.08)",
                  }}
                />
              ))}
            </div>
            <div className="flex justify-between mt-2 text-[10px] text-gray-600 tnum" style={{ letterSpacing: 0.8 }}>
              <span>DÉBUT {blockStart.toUpperCase()}</span>
              <span>FIN {blockEnd.toUpperCase()}</span>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* PARAMÈTRES */}
      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="px-4 mt-6">
        <SectionTitle label="Paramètres du programme" />
        <RowList>
          <Row icon={Target} tone="cyan" label="Objectif" value={getGoalLabel(raw.goal_id)} onClick={() => open("goal")} />
          <Row icon={Trophy} tone="cyan" label="Niveau" value={getLevelLabel(raw.level_id)} onClick={() => open("level")} />
          <Row icon={MapPin} tone="cyan" label="Lieu" value={getPlaceLabel(raw.place)} onClick={() => open("place")} />
          {/* Information, pas un éditeur : `training_days` n'est lu par aucune fonction de
              placement (chantier séparé). L'éditeur « days » plus bas reste en place pour ce
              chantier, mais n'est plus atteignable. */}
          <Row icon={CalendarDays} tone="cyan" label="Jours" value={PROGRAM_TRAINING_DAYS_LABEL} onClick={() => open("days_info")} info />
          <Row
            // Même illustration que la tuile « Poids » du Profil et la fenêtre « Pesée du jour ».
            illustration="/illustrations/poids.svg" tone="cyan" label="Poids cible"
            value={raw.target_weight != null ? `${formatFr(raw.target_weight, raw.target_weight % 1 === 0 ? 0 : 1)} kg` : "Non défini"}
            onClick={() => open("target_weight")}
          />
          <Row
            icon={Shield} tone="violet"
            label="Blessures et limitations"
            // Pas de promesse de remplacement d'exercices : ce chantier n'existe pas encore.
            // Ce qui est vrai aujourd'hui : l'admin lit ces informations dans la fiche client.
            sublabel="Visible par ton coach"
            value={injuriesValue}
            onClick={() => open("injuries")}
            last
          />
        </RowList>
        <Note>
          Modifier l'un de ces paramètres régénère ton programme. Le bloc en cours est conservé
          jusqu'à la fin de la semaine.
        </Note>
      </motion.section>

      <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }} className="px-4 mt-6">
        <RowList>
          <Row
            icon={RefreshCw} tone="violet"
            label="Changer de programme"
            sublabel={`Repartir sur un nouveau bloc de ${block.weeksTotal} semaines`}
            onClick={() => setEditing("restart")}
            last
          />
        </RowList>
      </motion.section>

      {/* ── Feuilles d'édition ── */}
      <EditSheet
        open={editing === "goal"} title="Objectif" onCancel={close} onSave={save} saving={saving}
        description="Change ce que le programme cherche à produire. Les objectifs proposés dépendent de ton niveau."
      >
        <SheetChoices
          value={draft}
          onChange={setDraft}
          options={getGoalsForLevel(raw.level_id).map((id) => ({ id, label: getGoalLabel(id) }))}
        />
      </EditSheet>

      <EditSheet
        open={editing === "level"} title="Niveau" onCancel={close} onSave={save} saving={saving}
        description="Le niveau détermine la structure des séances et les séances de test de force."
      >
        <SheetChoices
          value={draft}
          onChange={setDraft}
          options={Object.keys(LEVEL_LABELS).map((id) => ({ id, label: LEVEL_LABELS[id] }))}
        />
      </EditSheet>

      <EditSheet
        open={editing === "place"} title="Lieu d'entraînement" onCancel={close} onSave={save} saving={saving}
        description="Le matériel disponible change le choix des exercices."
      >
        <SheetChoices
          value={draft}
          onChange={setDraft}
          options={Object.keys(PLACE_LABELS).map((id) => ({ id, label: PLACE_LABELS[id] }))}
        />
      </EditSheet>

      <EditSheet
        open={editing === "days"} title="Jours d'entraînement" onCancel={close} onSave={save} saving={saving}
        description="Les jours où tu peux t'entraîner. Laisse vide pour ne pas contraindre le calendrier."
      >
        <div className="grid grid-cols-4 gap-2">
          {TRAINING_DAYS.map((d) => {
            const active = draftDays.includes(d.id)
            return (
              <button
                key={d.id}
                onClick={() => toggleDay(d.id)}
                className="py-3 rounded-xl text-[13px] font-semibold cursor-pointer transition-all active:scale-[0.97]"
                style={{
                  background: active ? "rgba(6,182,212,0.12)" : "rgba(255,255,255,0.02)",
                  border: `1px solid ${active ? "rgba(6,182,212,0.35)" : "rgba(30,41,59,0.55)"}`,
                  color: active ? "#67e8f9" : "#94a3b8",
                }}
              >
                {d.short}
              </button>
            )
          })}
        </div>
      </EditSheet>

      <EditSheet
        open={editing === "target_weight"} title="Poids cible" onCancel={close} onSave={save} saving={saving}
        description="Le poids que tu vises. Si ton objectif n'est pas un poids, choisis « Pas de poids cible »."
      >
        {!noTarget && (
          <div className="flex justify-center mb-3">
            <NumberWheel
              id="target-weight"
              label="Poids cible"
              labelHidden
              unit="kg"
              min={TARGET_WEIGHT.min}
              max={TARGET_WEIGHT.max}
              step={TARGET_WEIGHT.step}
              value={draftTarget}
              onChange={setDraftTarget}
            />
          </div>
        )}
        <button
          type="button"
          aria-pressed={noTarget}
          onClick={() => setNoTarget((v) => !v)}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left cursor-pointer transition-all active:scale-[0.99]"
          style={{
            background: noTarget ? "rgba(6,182,212,0.10)" : "rgba(255,255,255,0.02)",
            border: `1px solid ${noTarget ? "rgba(6,182,212,0.35)" : "rgba(30,41,59,0.55)"}`,
          }}
        >
          <span className="flex-1 text-[14px] font-medium" style={{ color: noTarget ? "#67e8f9" : "#e2e8f0" }}>Pas de poids cible</span>
          {noTarget && <Check className="w-4 h-4 text-cyan-400 shrink-0" />}
        </button>
      </EditSheet>

      <EditSheet
        open={editing === "injuries"} title="Blessures et limitations" onCancel={close} onSave={save} saving={saving}
        description="Décris ce qui te limite (articulation, mouvement douloureux, restriction médicale). Ton coach le voit dans ta fiche."
      >
        {injuries.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] text-gray-500 uppercase mb-2" style={{ letterSpacing: 1.5 }}>
              Déclarées dans ton diagnostic · {formatDateFR(injuries[0].created_at)}
            </p>
            <div className="rounded-xl" style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)" }}>
              {injuries.map((i, idx) => (
                <div key={i.id} className="px-3.5 py-2.5" style={{ borderTop: idx === 0 ? "none" : "1px solid rgba(30,41,59,0.55)" }}>
                  <p className="text-[14px] font-semibold text-white">{getInjuryZoneLabel(i.zone, i.zone_other)}</p>
                  <p className="text-[12.5px]" style={{ color: i.status === "genante" ? "#fbbf24" : "#9ca3af" }}>
                    {INJURY_STATUS_LABELS[i.status]}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        <label htmlFor="injuries-note" className="block text-[12px] text-gray-400 mb-1.5">
          {injuries.length > 0 ? "Ta précision" : "Ce qui te limite"}
        </label>
        <textarea
          id="injuries-note"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={4}
          maxLength={500}
          placeholder="Ex : épaule droite, pas de développé au-dessus de la tête"
          className="w-full px-3.5 py-3 rounded-xl text-[13.5px] text-white outline-none resize-none placeholder:text-gray-600"
          style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(30,41,59,0.55)", caretColor: "#06b6d4" }}
        />

        {/* Accord explicite — seulement si aucun n'est enregistré. Même texte (et même
            version) qu'au diagnostic. */}
        {!hasConsent && (
          <div className="mt-3 px-3.5 py-3 rounded-xl" style={{ background: "rgba(6,182,212,0.06)", border: "1px solid rgba(6,182,212,0.22)" }}>
            <div className="flex items-start gap-3">
              <input
                id="injuries-consent"
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="w-5 h-5 mt-0.5 shrink-0 cursor-pointer"
                style={{ accentColor: "#06b6d4" }}
              />
              <label htmlFor="injuries-consent" className="text-[13px] text-gray-200 leading-relaxed cursor-pointer">
                {INJURY_CONSENT_LABEL}
              </label>
            </div>
            <button
              type="button"
              aria-expanded={consentDetails}
              onClick={() => setConsentDetails((v) => !v)}
              className="ml-8 min-h-[44px] text-[12.5px] font-medium text-cyan-400 cursor-pointer"
            >
              {consentDetails ? "Masquer les détails" : "Détails"}
            </button>
            {consentDetails && (
              <p className="ml-8 text-[12px] text-gray-400 leading-relaxed">
                {INJURY_CONSENT_DETAILS}{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-400 underline">Politique de confidentialité</a>
              </p>
            )}
          </div>
        )}

        {injuries.length > 0 && (
          <p className="mt-3 text-[12.5px] text-gray-400 leading-relaxed">
            Une blessure a changé ?{" "}
            <a href={mailtoHref("Mes blessures ont changé")} className="font-semibold text-cyan-400">Écris-moi</a>, je mets ta fiche à jour.
          </p>
        )}

        {hasConsent && (
          confirmRevoke ? (
            <div className="mt-4 p-3.5 rounded-xl" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)" }}>
              <p className="text-[12.5px] text-gray-200 leading-relaxed">
                Tes blessures déclarées, ta précision et ton accord seront effacés. Ton programme ne pourra plus en tenir compte.
              </p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setConfirmRevoke(false)}
                  disabled={saving}
                  className="min-h-[44px] rounded-xl text-[13px] font-semibold text-gray-300 cursor-pointer disabled:opacity-50"
                  style={{ border: "1px solid rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.03)" }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={revoke}
                  disabled={saving}
                  className="min-h-[44px] rounded-xl text-[13px] font-bold text-white cursor-pointer disabled:opacity-60"
                  style={{ background: "linear-gradient(135deg,#dc2626,#ef4444)" }}
                >
                  Tout effacer
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmRevoke(true)}
              className="mt-4 w-full min-h-[48px] rounded-xl text-[13.5px] font-semibold cursor-pointer"
              style={{ background: "transparent", border: "1px solid rgba(248,113,113,0.35)", color: "#f87171" }}
            >
              Retirer mon accord et effacer ces informations
            </button>
          )
        )}
      </EditSheet>

      {/* Information — la ligne « Jours » n'ouvre plus d'éditeur (lot 3). ⚠️ À réécrire avec
          le chantier training_days : ce texte dit que chaque semaine reprend les 4 mêmes jours. */}
      <EditSheet open={editing === "days_info"} title="Tes jours d'entraînement" onCancel={close}>
        <div className="space-y-3 text-[13.5px] text-gray-300 leading-relaxed">
          <p>Tes séances sont placées automatiquement le lundi, le mardi, le jeudi et le vendredi.</p>
          <p>
            Pour changer le jour d'une séance, touche-la dans ton calendrier, puis « Déplacer ». Chaque nouvelle
            semaine de séances reprend ces quatre jours.
          </p>
        </div>
        <div className="grid gap-2.5 mt-5">
          <button
            type="button"
            onClick={() => navigate("/dashboard/calendar")}
            className="min-h-[50px] rounded-xl text-[14px] font-semibold cursor-pointer"
            style={{ background: "rgba(6,182,212,0.15)", border: "1px solid rgba(6,182,212,0.35)", color: "#22d3ee" }}
          >
            Ouvrir le calendrier
          </button>
          <button
            type="button"
            onClick={close}
            className="min-h-[50px] rounded-xl text-[14px] font-semibold text-gray-200 cursor-pointer"
            style={{ border: "1px solid rgba(255,255,255,0.12)", background: "transparent" }}
          >
            Fermer
          </button>
        </div>
      </EditSheet>

      <EditSheet
        open={editing === "restart"} title="Changer de programme" onCancel={close}
        description="Cette action se fait avec ton coach : elle remet le compteur de bloc à zéro et repart d'un test de force initial."
      >
        <button
          onClick={() => {
            window.location.href = mailtoHref("Changer de programme")
          }}
          className="w-full py-3 rounded-xl text-[14px] font-bold text-white cursor-pointer"
          style={{ background: "linear-gradient(135deg,#0369a1,#06b6d4)" }}
        >
          En parler au coach
        </button>
      </EditSheet>

      <BottomNav />
    </div>
  )
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s
}
